// src/theme.js
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark', // ensures dark defaults

    // 🔹 Brand colors
    primary: {
      main: '#FFF',
      // main: '#FFF192', // bright yellow accent
      contrastText: '#fff',
    },
    secondary: {
      main: '#7ACFFF', // soft blue accent
      contrastText: '#000000',
    },

    // 🔸 Feedback colors
    error: {
      main: '#ff5f5f',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#fff',
    },
    warning: {
      main: '#ffa726',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000',
    },
    info: {
      main: '#29b6f6',
      light: '#4fc3f7',
      dark: '#0288d1',
      contrastText: '#000',
    },
    success: {
      main: '#66bb6a',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#000',
    },

    // ⚫ Backgrounds
    background: {
      default: '#121212', // overall page background
      paper: '#3a3a3a', // card, input background
    },

    // ✍️ Text
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255,255,255,0.7)',
      disabled: 'rgba(255,255,255,0.5)',
    },

    // ➖ Dividers
    divider: 'rgba(255,255,255,0.12)',

    // ⚙️ Interactive action states
    action: {
      active: '#fff',
      hover: 'rgba(255,241,146,0.08)',
      selected: 'rgba(255,241,146,0.16)',
      disabled: 'rgba(255,255,255,0.3)',
      disabledBackground: 'rgba(255,255,255,0.12)',
      focus: 'rgba(255,241,146,0.12)',
    },
  },

  typography: {
    fontFamily: 'futura-pt, sans-serif',
    fontSize: 14,
  },

  components: {
    // 🧱 Global Autofill & Input Styles
    MuiCssBaseline: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0px 1000px rgba(58,58,58,1) inset !important',
            WebkitTextFillColor: 'white !important',
            caretColor: '#fff',
          },
        },
      },
    },

    // 🧱 Outlined TextFields
    MuiTextField: {
      styleOverrides: {
        root: {
          color: '#fff',
          backgroundColor: 'rgba(58, 58, 58, 1)',
          borderRadius: 8,
          '& .MuiOutlinedInput-input': {
            padding: '10px 14px',
            color: '#fff',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.3) !important',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white !important',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white !important',
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255,255,255,0.8)',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'rgba(255,255,255,0.8)',
          },
        },
      },
    },

    // 🧱 Select components
    MuiSelect: {
      styleOverrides: {
        root: {
          color: '#fff',
          backgroundColor: 'rgba(58,58,58,1)',
          borderRadius: 8,
          '& .MuiSelect-select': {
            padding: '10px 14px !important',
            color: '#fff',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.3) !important',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white !important',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white !important',
          },
          '& .MuiSelect-icon': {
            color: '#fff',
          },
        },
      },
    },

    // 🧱 Checkbox
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.3)',
          '&.Mui-checked': {
            color: '#fff',
          },
          '&.Mui-error': {
            color: '#d32f2f',
          },
        },
      },
    },

    // 🧱 Radio
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#fff',
          '&.Mui-checked': {
            color: '#fff', // custom accent
            // color: '#FFF192', // custom accent
          },
        },
      },
    },

    // 🧱 FormLabel (used in radio groups)
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.8)',
          '&.Mui-focused': {
            color: 'rgba(255,255,255,0.8)', // no color change on focus
          },
        },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: 'rgba(255,255,255,0.7)',
          fontSize: '1rem',
          letterSpacing: '0.5px',
          transition: 'color 0.2s ease',

          '&.Mui-active *': {
            // color: '#FFF192',
            fontWeight: 600,
          },

          '&.Mui-completed': {
            color: 'info',
          },

          '&.Mui-error': {
            color: 'error',
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          '*': {
            color: 'rgba(255,255,255,0.3)',
          },
          '&.Mui-active circle': {
            fill: 'white',
            // fill: '#FFF192',
          },
          '&.Mui-active text': {
            fill: '#000',
          },
          '&.Mui-completed *': {
            fill: 'white',
            // fill: '#FFF192',
          },
          '&.Mui-completed text': {
            fill: '#000',
          },
        },
      },
    },
  },
});

export default theme;
